/**
 * /wireline-slickline/sheets/:id — the office review of one day sheet
 * (GET / PATCH / DELETE /sheets/:id, POST …/submit | approve | return,
 * fieldiq-api#1338).
 *
 * A draft is edited here (window, crew, equipment with the hour meter) and
 * submitted; a submitted sheet is approved or returned to the crew WITH A
 * REASON (the API requires one, and the crew sees it on the sheet). An
 * approved sheet is locked. Every warning the API sends — a missing rig-up
 * test, a misfire count that disagrees, a meter below its previous reading, a
 * footage mismatch on approval — is shown and never blocks.
 *
 * Writing a sheet is WL_SHEET_WRITE; approve, return and delete are
 * WL_SHEET_APPROVE. The run log PDF of the sheet's runs (WL-3, api#1339) is
 * any reader's.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Activity, ClipboardList, Clock, Download, Pencil, Send, Trash2, Undo2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useWlEquipmentOptions, useWlMeta, useWlUsers } from '../../hooks/useWirelineSlickline';
import {
  useApproveWlSheet,
  useDeleteWlSheet,
  useReturnWlSheet,
  useSaveWlSheet,
  useSubmitWlSheet,
  useWlJob,
  useWlSheet,
} from '../../hooks/useWlJobs';
import { claimBlobTab } from '../../lib/blob-tab';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { openWlSheetRunLogPdf } from '../../lib/wireline-slickline-reports';
import { canApproveWlSheets, canReadWlRentIns, canWriteWlSheets, wlCanRecordOnSheet, wlDay, wlHours, wlJobRefusal, wlSheetStatusSkin } from '../../lib/wireline-slickline-jobs';
import type { WlWarningEntry } from '../../types/wireline-slickline-jobs';
import { useBackToList } from '../downhole/recordPage';
import { SheetLinksPanel } from './SheetLinks';
import { canReadWlTickets } from '../../lib/wireline-slickline-tickets';
import { WlSheetTicketLink } from './tickets/SheetTicketLink';
import { canUseWlAi } from '../../lib/wl-ai';
import { WlAiLauncher } from './ai/WlAiBits';
import { SheetEditor, SheetReadout, SheetRuns, SheetSummary, SheetWarnings } from './SheetParts';
import {
  isNotFound,
  WL_BAND_SECONDARY,
  WL_EYEBROW,
  WL_INPUT,
  WL_PRIMARY,
  WL_SECONDARY,
  WlBadge,
  WlDialog,
  WlEmpty,
  WlField,
  WlFormError,
  WlLoading,
  WlPanel,
} from './WlBits';

export const wlRunPath = (id: string) => `/wireline-slickline/runs/${id}`;

export default function WlSheetScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const query = useWlSheet(id);
  const sheet = query.data?.data ?? null;
  const back = useBackToList(sheet ? `/wireline-slickline/jobs/${sheet.jobId}` : '/wireline-slickline/jobs');
  const catalog = useWlMeta().data?.data ?? null;
  const canWrite = canWriteWlSheets(user?.role);
  const canApprove = canApproveWlSheets(user?.role);
  const draft = !!sheet && sheet.status === 'draft' && !sheet.locked;
  const [editing, setEditing] = useState(false);
  const users = useWlUsers(canWrite && editing);
  const equipment = useWlEquipmentOptions(canWrite && editing);
  const job = useWlJob(editing && canReadWlRentIns(user?.role) ? sheet?.jobId : undefined);
  const save = useSaveWlSheet();
  const submit = useSubmitWlSheet();
  const approve = useApproveWlSheet();
  const giveBack = useReturnWlSheet();
  const del = useDeleteWlSheet();
  const [editError, setEditError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [writeWarnings, setWriteWarnings] = useState<WlWarningEntry[]>([]);
  const [returning, setReturning] = useState(false);
  const [reason, setReason] = useState('');
  const [returnError, setReturnError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    setEditing(false);
    setActionError(null);
    setWriteWarnings([]);
  }, [id]);

  /** WL-3: every run on the sheet as one run log PDF. The tab is claimed before the await. */
  const runLogPdf = async () => {
    setActionError(null);
    const err = await openWlSheetRunLogPdf(sheet!.id, claimBlobTab());
    if (err) setActionError(err);
  };

  const failure = queryLoadFailure(query);
  const notFound = isNotFound(queryFailureError(query));
  const busy = submit.isPending || approve.isPending || giveBack.isPending;

  const act = (fn: typeof submit, ok: string, fallback: string) => {
    setActionError(null);
    setWriteWarnings([]);
    fn.mutate(sheet!.id, {
      onSuccess: (res) => {
        toast.success(ok);
        setWriteWarnings(res.warnings);
      },
      onError: (err) => setActionError(wlJobRefusal(err, fallback)),
    });
  };

  return (
    <div data-testid="wl-sheet-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WL_EYEBROW}
        title={sheet ? `${sheet.jobNumber ?? 'Job'} · ${wlDay(sheet.workDate)}` : query.isLoading ? 'Day sheet' : '—'}
        onBack={back}
        backLabel="Back"
        actions={
          sheet ? (
            <div className="flex flex-wrap gap-2">
              {draft && canWrite && !editing && (
                <button type="button" className={WL_BAND_SECONDARY} onClick={() => { setEditError(null); setEditing(true); }} data-testid="wl-sheet-edit">
                  <Pencil className="size-4" aria-hidden="true" />
                  Edit sheet
                </button>
              )}
              {draft && canWrite && (
                <button type="button" className="fi-action fi-action--standard fi-action--primary" disabled={busy} onClick={() => act(submit, 'Sheet submitted', 'The sheet could not be submitted.')} data-testid="wl-sheet-submit">
                  <Send className="size-4" aria-hidden="true" />
                  Submit
                </button>
              )}
              {sheet.status === 'submitted' && canApprove && (
                <>
                  <button type="button" className="fi-action fi-action--standard fi-action--primary" disabled={busy} onClick={() => act(approve, 'Sheet approved', 'The sheet could not be approved.')} data-testid="wl-sheet-approve">
                    <CheckCircle2 className="size-4" aria-hidden="true" />
                    Approve
                  </button>
                  <button type="button" className={WL_BAND_SECONDARY} disabled={busy} onClick={() => { setReason(''); setReturnError(null); setReturning(true); }} data-testid="wl-sheet-return">
                    <Undo2 className="size-4" aria-hidden="true" />
                    Return to crew
                  </button>
                </>
              )}
              {sheet.runs.length > 0 && (
                <button type="button" className={WL_BAND_SECONDARY} onClick={runLogPdf} data-testid="wl-sheet-run-log-pdf">
                  <Download className="size-4" aria-hidden="true" />
                  Run log PDF
                </button>
              )}
              {draft && canApprove && (
                <button type="button" className={WL_BAND_SECONDARY} onClick={() => { setDeleteError(null); setDeleting(true); }}>
                  <Trash2 className="size-4" aria-hidden="true" />
                  Delete
                </button>
              )}
            </div>
          ) : undefined
        }
        stats={[
          {
            icon: <Activity className="size-4" aria-hidden="true" />,
            label: 'Status',
            tone: 'neutral',
            loading: query.isLoading,
            value: sheet ? sheet.statusLabel ?? wlSheetStatusSkin(sheet.status).label : '—',
          },
          { icon: <Clock className="size-4" aria-hidden="true" />, label: 'Sheet hours', tone: 'neutral', loading: query.isLoading, value: wlHours(sheet?.sheetHours) },
          {
            icon: <ClipboardList className="size-4" aria-hidden="true" />,
            label: 'Runs',
            tone: 'accent',
            loading: query.isLoading,
            value: sheet?.totals?.runCount === null || sheet?.totals?.runCount === undefined ? '—' : String(sheet.totals.runCount),
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <WlLoading label="Loading day sheet" />
          ) : notFound || (!failure && !sheet) ? (
            <WlPanel eyebrow="Day sheet" title="Day sheet">
              <WlEmpty>This day sheet was not found. It may have been deleted.</WlEmpty>
            </WlPanel>
          ) : failure || !sheet ? (
            <WlPanel eyebrow="Day sheet" title="Day sheet">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this day sheet" />
            </WlPanel>
          ) : (
            <>
              <WlFormError message={actionError} />
              {sheet.returnedReason && sheet.status === 'draft' && (
                <p className="rounded-fi-control border border-fi-signal-amber/40 bg-fi-signal-amber/10 px-3 py-2 text-sm text-fieldiq-text" data-testid="wl-sheet-returned">
                  Returned by the office: {sheet.returnedReason}
                </p>
              )}
              <SheetWarnings sheet={sheet} extra={writeWarnings} />
              <WlPanel eyebrow="Day sheet" title="Sheet" testId="wl-sheet-header">
                <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                  <div>
                    <dt className="text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Status</dt>
                    <dd className="mt-1">
                      <WlBadge skin={wlSheetStatusSkin(sheet.status, sheet.statusLabel)} value={sheet.status} />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Job</dt>
                    <dd className="mt-1 text-sm">
                      <Link to={`/wireline-slickline/jobs/${sheet.jobId}`} className="font-semibold text-fi-amber underline">
                        {sheet.jobNumber ?? '—'}
                      </Link>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Unit</dt>
                    <dd className="mt-1 text-sm text-white">{sheet.unitNumber || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Customer · well</dt>
                    <dd className="mt-1 text-sm text-white">
                      {sheet.customerName || '—'} · {sheet.wellName || '—'}
                    </dd>
                  </div>
                </dl>
                {(sheet.submittedByName || sheet.approvedByName) && (
                  <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">
                    {sheet.submittedByName ? `Submitted by ${sheet.submittedByName}` : ''}
                    {sheet.submittedByName && sheet.approvedByName ? ' · ' : ''}
                    {sheet.approvedByName ? `Approved by ${sheet.approvedByName}` : ''}
                  </p>
                )}
              </WlPanel>

              <SheetSummary sheet={sheet} />

              {editing && draft && canWrite ? (
                <SheetEditor
                  key={sheet.updatedAt ?? sheet.id}
                  sheet={sheet}
                  catalog={catalog}
                  users={users.data ?? []}
                  equipment={(equipment.data ?? []).map((e) => ({ id: e.id, name: e.unitNumber ? `${e.name} · ${e.unitNumber}` : e.name }))}
                  rentIns={(job.data?.data?.rentIns ?? []).map((r) => ({ id: r.id, name: `${r.description} · ${r.vendorName}` }))}
                  canEditCrew={user?.role !== 'field_worker' || wlCanRecordOnSheet(sheet, user?.id)}
                  pending={save.isPending}
                  error={editError}
                  onSubmit={(body) => {
                    setEditError(null);
                    save.mutate(
                      { id: sheet.id, body },
                      {
                        onSuccess: (res) => {
                          toast.success('Sheet saved');
                          setWriteWarnings(res.warnings);
                          setEditing(false);
                        },
                        onError: (err) => setEditError(wlJobRefusal(err, 'The sheet could not be saved.')),
                      },
                    );
                  }}
                />
              ) : (
                <SheetReadout sheet={sheet} />
              )}
              {editing && (
                <div className="flex justify-end">
                  <button type="button" className={WL_SECONDARY} onClick={() => setEditing(false)}>
                    Stop editing
                  </button>
                </div>
              )}

              <SheetLinksPanel sheet={sheet} editable={draft && canWrite} />
              {sheet.status === 'office_approved' && canReadWlTickets(user?.role) && (
                <WlSheetTicketLink sheetId={sheet.id} jobId={sheet.jobId} workDate={sheet.workDate} unitId={sheet.unitId ?? null} />
              )}
              <SheetRuns sheet={sheet} runPath={wlRunPath} newRunPath={`/wireline-slickline/sheets/${sheet.id}/runs/new`} canAdd={draft && canWrite} />
              {canUseWlAi(user?.role) && (
                <WlAiLauncher
                  key={sheet.id}
                  request={{ agent: 'wl_sheet_summary', sheetId: sheet.id }}
                  eyebrow="AI draft"
                  title="Day-sheet summary"
                  blurb="Totals this sheet's hours, runs, footage, shots, plugs and crew exactly, then asks the AI to draft a summary. The draft waits for your approval; nothing is sent."
                  testId="wl-ai-sheet-summary"
                />
              )}
            </>
          )}
        </div>
      </section>

      {sheet && (
        <WlDialog
          open={returning}
          title="Return the sheet to the crew"
          onClose={() => setReturning(false)}
          footer={
            <>
              <button type="button" className={WL_SECONDARY} onClick={() => setReturning(false)} disabled={giveBack.isPending}>
                Keep it
              </button>
              <button
                type="button"
                className={WL_PRIMARY}
                disabled={giveBack.isPending || reason.trim() === ''}
                data-testid="wl-sheet-return-confirm"
                onClick={() =>
                  giveBack.mutate(
                    { id: sheet.id, reason: reason.trim() },
                    {
                      onSuccess: () => {
                        toast.success('Sheet returned to the crew');
                        setReturning(false);
                      },
                      onError: (err) => setReturnError(wlJobRefusal(err, 'The sheet could not be returned.')),
                    },
                  )
                }
              >
                {giveBack.isPending ? 'Returning…' : 'Return sheet'}
              </button>
            </>
          }
        >
          <div className="space-y-3">
            <WlFormError message={returnError} />
            <WlField label="Reason" hint="The crew sees this on the sheet">
              <textarea className={`${WL_INPUT} h-24 py-2`} aria-label="Return reason" value={reason} onChange={(e) => setReason(e.target.value)} />
            </WlField>
          </div>
        </WlDialog>
      )}

      {sheet && (
        <WlDialog
          open={deleting}
          title="Delete this day sheet"
          onClose={() => setDeleting(false)}
          footer={
            <>
              <button type="button" className={WL_SECONDARY} onClick={() => setDeleting(false)} disabled={del.isPending}>
                Keep it
              </button>
              <button
                type="button"
                className={WL_PRIMARY}
                disabled={del.isPending}
                onClick={() =>
                  del.mutate(sheet.id, {
                    onSuccess: () => {
                      toast.success('Day sheet deleted');
                      navigate(`/wireline-slickline/jobs/${sheet.jobId}`, { replace: true });
                    },
                    onError: (err) => setDeleteError(wlJobRefusal(err, 'The sheet could not be deleted.')),
                  })
                }
              >
                {del.isPending ? 'Deleting…' : 'Delete sheet'}
              </button>
            </>
          }
        >
          <WlFormError message={deleteError} />
          <p className="text-sm text-fieldiq-text">Only a draft sheet can be deleted.</p>
        </WlDialog>
      )}
    </div>
  );
}
